import { formatStamp } from '@baseline/shared';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { useDashboard, useFreshness } from '../api/queries.js';
import { useMe, useSession } from '../api/session.js';
import { CommandPalette } from '../components/CommandPalette.js';
import { Chevron, Logo, NavIcon, SearchIcon } from '../components/icons.js';
import { useLiveUpdates } from '../live/useLiveUpdates.js';

import type { NavKey } from '../components/icons.js';

// Prototype app shell: dark top bar with the user menu, sidebar with search, nav, shortcuts
// and the freshness footer, and the page in `main` (plan Phase 10).

const NAV: [NavKey, string, string][] = [
  ['dashboard', 'Dashboard', '/'],
  ['alerts', 'Alerts', '/alerts'],
  ['compare', 'Compare', '/compare'],
];

export function AppShell() {
  const me = useMe();
  const { logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboard = useDashboard();
  const freshness = useFreshness();
  const [menu, setMenu] = useState(false);
  const [pal, setPal] = useState(false);
  const main = useRef<HTMLElement>(null);
  const who = useRef<HTMLDivElement>(null);
  useLiveUpdates();

  const d = dashboard.data;
  const need = d?.stages.find((s) => s.statusKey === 'new')?.count ?? 0;
  const high = d?.severityCounts[2] ?? 0;
  const waiting = d?.kpis.waiting ?? 0;

  const reads = [freshness.data?.pdmLastReadAt, freshness.data?.erpLastReadAt].filter(
    (x): x is string => typeof x === 'string',
  );
  const lastRead = reads.length ? reads.sort()[0] : null;

  // Ctrl/⌘+K opens the palette from anywhere (prototype keyboard handler).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPal((p) => !p);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // The user menu closes on any click outside it.
  useEffect(() => {
    if (!menu) return;
    const onClick = (e: MouseEvent) => {
      if (!who.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [menu]);

  // Every page starts at the top, like the prototype's `go()`.
  useEffect(() => {
    main.current?.scrollTo?.(0, 0);
    setMenu(false);
  }, [location.pathname]);

  const nv = (key: NavKey, label: string, to: string, badge?: number) => (
    <NavLink
      key={key}
      to={to}
      end={to === '/'}
      className={({ isActive }) => `nv${isActive ? ' on' : ''}`}
    >
      <NavIcon name={key} />
      <span>{label}</span>
      {badge ? <em>{badge}</em> : null}
    </NavLink>
  );

  return (
    <>
      <div className="app" id="app">
        <header className="top">
          <div className="brand">
            <Logo size={30} variant="light" />
            Baseline
          </div>
          <div className="top-r" ref={who}>
            <div id="pop" hidden={!menu}>
              <div className="pm">
                <b style={{ color: 'var(--ink)' }}>{me.name}</b>
                <br />
                {me.email}
                <br />
                {me.title}
              </div>
              <button id="signout" type="button" onClick={() => void logout()}>
                Sign out
              </button>
            </div>
            <button
              id="who"
              type="button"
              aria-haspopup="true"
              aria-expanded={menu}
              onClick={() => setMenu((m) => !m)}
            >
              <span className="av">{me.initials}</span>
              <span className="who-n">{me.name}</span>
              <Chevron />
            </button>
          </div>
        </header>
        <aside className="side">
          <button
            className="srch"
            id="open-pal"
            type="button"
            aria-label="Search alerts and pages (Ctrl/⌘ + K)"
            title="Search (Ctrl/⌘ + K)"
            onClick={() => setPal(true)}
          >
            <SearchIcon />
            <span>Search…</span>
          </button>
          <nav id="nav" aria-label="Primary">
            <div className="nav-g">
              {NAV.map(([key, label, to]) =>
                nv(key, label, to, key === 'alerts' ? need : undefined),
              )}
            </div>
            <div className="nav-l nav-x">Shortcuts</div>
            <div className="nav-g nav-x">
              <button
                className="nv"
                type="button"
                onClick={() => void navigate('/alerts?severity=2')}
              >
                <i className="dot" style={{ background: 'var(--red)' }} />
                <span>High severity</span>
                <em>{high}</em>
              </button>
              <button
                className="nv"
                type="button"
                onClick={() => void navigate('/alerts?view=waiting')}
              >
                <i className="dot" style={{ background: 'var(--mint)' }} />
                <span>Waiting for reply</span>
                <em>{waiting}</em>
              </button>
            </div>
            <div className="nav-l nav-x">Settings</div>
            <div className="nav-g nav-x">{nv('connectors', 'Connectors', '/connectors')}</div>
          </nav>
          <div className="fresh">
            <span className="fd" />
            <span>
              PDM and ERP last read on
              <br />
              {lastRead ? formatStamp(lastRead) : '—'}
            </span>
          </div>
        </aside>
        <main className="main" id="main" ref={main}>
          <div id="page">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette open={pal} onClose={() => setPal(false)} />
    </>
  );
}
